import React from 'react';
import { 
  Bot, 
  Mic, 
  MessageCircle,
  RotateCcw
} from 'lucide-react';
import './AudioComponent.css';

export type GameState = 'initial' | 'robot-speaking' | 'robot-thinking' | 'mic-ready' | 'mic-listening' | 'mic-processing' | 'conversation-finished';

interface AudioComponentProps {
  gameState: GameState;
  onStartGame: () => void;
  onStartListening: () => void;
  onStopListening: () => void;
  onNewConversation?: () => void;
  disabled?: boolean;
  className?: string;
  error?: string | null;
  chatRetryState?: {
    isRetryMode: boolean;
    hasRetried: boolean;
    lastUserMessage?: string;
    lastUserAudioBlob?: Blob | null;
  };
  onRetryLastMessage?: () => void;
  onAskNewQuestion?: () => void;
  onClearError?: () => void;
  onSetGameState?: (state: GameState) => void;
  onReplayLastMessage?: () => void;
  hasAssistantMessages?: boolean;
}

const AudioComponent: React.FC<AudioComponentProps> = ({
  gameState,
  onStartGame,
  onStartListening,
  onStopListening,
  onNewConversation,
  disabled = false,
  className = '',
  error,
  chatRetryState,
  onRetryLastMessage,
  onAskNewQuestion,
  onClearError,
  onSetGameState,
  onReplayLastMessage,
  hasAssistantMessages = false
}) => {
  const renderContent = () => {
    switch (gameState) {
      case 'initial':
        return (
          <div className="start-game-section">
            <button
              className="start-game-btn"
              onClick={onStartGame}
              disabled={disabled}
            >
              Start
            </button>
          </div>
        );
        
      case 'robot-speaking':
      case 'robot-thinking':
        return (
          <div className="robot-turn-section">
            <div className={`robot-icon-large ${gameState === 'robot-thinking' ? 'thinking' : 'speaking'}`}>
              <Bot size={64} />
            </div>
            <div className="robot-status-large">
              {gameState === 'robot-speaking' ? 'Aan het praten...' : 'Aan het nadenken...'}
            </div>
          </div>
        );
        
      case 'mic-ready':
      case 'mic-listening':
      case 'mic-processing':
        return (
          <div className="mic-turn-section">
            {error && chatRetryState?.isRetryMode && (
              <div className="error-popup">
                <div className="error-message">{error}</div>
                <div className="retry-options">
                  {!chatRetryState.hasRetried && (
                    <button 
                      onClick={() => {
                        onClearError?.();
                        onRetryLastMessage?.();
                      }}
                      className="retry-button"
                    >
                      Probeer opnieuw
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      onAskNewQuestion?.();
                      onSetGameState?.('mic-ready');
                    }}
                    className="new-question-button"
                  >
                    Stel andere vraag
                  </button>
                </div>
              </div>
            )}
            <div className="mic-with-replay-container">
              {hasAssistantMessages && onReplayLastMessage && gameState !== 'mic-listening' && (
                <button
                  className="replay-button"
                  onClick={onReplayLastMessage}
                  disabled={disabled}
                  title="Luister laatste bericht opnieuw"
                >
                  <RotateCcw size={20} />
                </button>
              )}
              <button
                className={`microphone-btn-large ${
                  gameState === 'mic-listening' ? 'listening' : 
                  gameState === 'mic-ready' ? 'ready' : 'processing'
                }`}
                onClick={gameState === 'mic-listening' ? onStopListening : onStartListening}
                disabled={disabled}
              >
                <div className="mic-icon-large">
                  <Mic size={48} />
                </div>
                {gameState === 'mic-listening' && <div className="pulse-ring-large"></div>}
              </button>
            </div>
            <div className="mic-status-large">
              {gameState === 'mic-processing' ? 'Verwerken...' : 
               gameState === 'mic-listening' ? 'Klik om te stoppen' : 
               'Klik om te spreken'}
            </div>
          </div>
        );
        
      case 'conversation-finished':
        return (
          <div className="conversation-finished-section">
            <div className="finished-icon-large">
              <MessageCircle size={64} />
            </div>
            <div className="finished-status-large">
              Gesprek afgerond
            </div>
            {onNewConversation && (
              <button
                className="new-conversation-btn"
                onClick={onNewConversation}
                disabled={disabled}
              >
                Nieuw gesprek
              </button>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className={`audio-component ${className || ''}`}>
      <div className="audio-controls-new">
        {renderContent()}
      </div>
    </div>
  );
};

export default AudioComponent;